// BM-H3849 (7 Oct 2026): UAT matters.
//
// A matter can be marked as a UAT matter by any signed-in member of staff (Vanessa, 8 Oct: the team
// marks it, so she does not have to). While it is marked, anyone on the team can take every client
// care letter step on it (the server decides, see POST /submissions/{id}/uat-matter). Everyone sees
// a badge saying so, so nobody takes a step on a real matter believing it is a test, and the reason
// (at least ten characters) is recorded with who and when. The server refuses to mark a letter a
// client has already signed; this component only reports what it says.
//
// BM-H3978 (8 Oct 2026): the mark now also lets anyone on the team take the private client (Wills)
// steps on the matter, stamped as taken under the mark, and those steps stop counting if the mark is
// taken off. This is the ONE component; it is mounted under the client care letter editor and, for
// every matter type, near the top of the matter page (MatterUatStrip). The mark has to be placed
// BEFORE a client signs the letter: afterwards the server refuses it, so `clientSigned` says so
// up front instead of waiting for a refusal.
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";

export const UAT_REASON_MIN = 10;

export default function UatMatterToggle({ submissionId, uatMatter = false, markedBy = "", onChange, clientSigned = false }) {
  const [on, setOn] = useState(!!uatMatter);
  const [by, setBy] = useState(markedBy);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const trimmed = reason.trim();
  const reasonOk = trimmed.length >= UAT_REASON_MIN;
  const turningOn = !on;

  const submit = async () => {
    if (!reasonOk || busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/submissions/${submissionId}/uat-matter`, { enabled: turningOn, reason: trimmed });
      setOn(turningOn);
      if (!turningOn) setBy("");
      setOpen(false);
      setReason("");
      toast.success(turningOn ? "Marked as a UAT matter" : "UAT mark taken off");
      onChange?.(turningOn);
    } catch (err) {
      toast.error(errorMessage(err, "Could not change the UAT mark"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-2 px-4 py-2 border-t border-slate-200 bg-amber-50 text-xs" data-testid="uat-matter-strip">
      {on ? (
        <span className="text-amber-900" data-testid="uat-matter-badge">
          UAT matter{by ? `, marked by ${by}` : ""}. Anyone on the team can take every step on this matter's client care
          letter and Wills confirmations. Each step is recorded as taken under the UAT mark.
        </span>
      ) : (
        <span className="text-slate-600">
          This is a real matter. Only the fee earner or a director can review the letter, and only the people named on each
          Wills confirmation can confirm it.
          {clientSigned
            ? " A client has already signed the client care letter, so this matter can no longer be marked as a UAT matter."
            : " To run a UAT on it, mark it BEFORE a client signs the letter."}
        </span>
      )}
      {!open && (!clientSigned || on) && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="ml-auto px-2 py-1 rounded border border-amber-400 text-amber-900 hover:bg-amber-100"
          data-testid="uat-matter-toggle-btn"
        >
          {on ? "Take off UAT mark" : "Mark as UAT matter"}
        </button>
      )}
      {open && (
        <div className="ml-auto flex items-center gap-2" data-testid="uat-matter-form">
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={turningOn ? "Why (at least 10 characters)" : "Why it is coming off (at least 10 characters)"}
            className="border border-slate-300 rounded px-2 py-1 w-72 bg-white"
            data-testid="uat-matter-reason"
          />
          <button
            type="button"
            onClick={submit}
            disabled={!reasonOk || busy}
            className="px-2 py-1 rounded bg-amber-600 text-white disabled:opacity-50"
            data-testid="uat-matter-confirm"
          >
            {busy ? "Saving" : "Confirm"}
          </button>
          <button type="button" onClick={() => { setOpen(false); setReason(""); }} className="px-2 py-1 rounded border border-slate-300" data-testid="uat-matter-cancel">
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
